import type { Contact, Lead, MessageThread } from "./crm";
import type { ChatWidgetQuestions } from "./chat-widget-config";

export const CHAT_WIDGET_SOURCE = "Chat do site" as const;
export const CHAT_SERVICE_OPTIONS = [
  "Sites e landing pages",
  "Lojas virtuais",
  "Apps e sistemas",
  "Marca e identidade visual",
  "Conteúdo, copy e SEO",
  "Área de membros e lançamentos",
  "Outro",
] as const;
export const CHAT_TIMEFRAME_OPTIONS = [
  "O quanto antes",
  "Em 1–3 meses",
  "Em 3–6 meses",
  "Só pesquisando",
] as const;
export const CHAT_BUDGET_OPTIONS = [
  "Até R$ 5 mil",
  "R$ 5–10 mil",
  "R$ 10–20 mil",
  "Acima de R$ 20 mil",
  "Ainda não sei",
] as const;
export const CHAT_ALLOWED_PAGE_HOSTS = [
  "studiotche.com",
  "www.studiotche.com",
  "studiotche.com.br",
  "www.studiotche.com.br",
] as const;
export const CHAT_ALLOWED_LOCAL_PAGE_ORIGINS = [
  "http://localhost:4321",
] as const;
export const CHAT_PRIVACY_URL =
  "https://studiotche.com/politica-de-privacidade";

export type ChatService = (typeof CHAT_SERVICE_OPTIONS)[number];
export type ChatTimeframe = (typeof CHAT_TIMEFRAME_OPTIONS)[number];
export type ChatBudget = (typeof CHAT_BUDGET_OPTIONS)[number];

export interface ChatLeadSubmission {
  readonly submissionId: string;
  readonly services: readonly ChatService[];
  readonly project: string;
  readonly timeframe: ChatTimeframe;
  readonly budget: ChatBudget;
  readonly name: string;
  readonly phone: string;
  readonly email?: string;
  readonly pageUrl: string;
  readonly website?: string;
}

export type ChatSubmissionValidation =
  | { readonly ok: true; readonly value: ChatLeadSubmission }
  | { readonly ok: false; readonly error: string };

export interface ChatRecordBundle {
  readonly lead: Lead;
  readonly contact: Contact;
  readonly message: MessageThread;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const trimmedString = (value: unknown, maxLength: number): string | null => {
  if (typeof value !== "string") return null;
  const clean = value.trim();
  return clean.length <= maxLength ? clean : null;
};

const isOneOf = <T extends string>(
  value: string,
  options: readonly T[],
): value is T => options.includes(value as T);

export const phoneDigits = (phone: string): string => phone.replace(/\D/g, "");

export const isValidBrazilianPhone = (phone: string): boolean => {
  const digits = phoneDigits(phone);
  return /^[1-9]{2}\d{8,9}$/.test(digits);
};

export const parseChatLeadSubmission = (
  input: unknown,
  allowDevelopmentPages = false,
): ChatSubmissionValidation => {
  const body = asRecord(input);
  if (!body) return { ok: false, error: "Envio inválido." };

  const submissionId = trimmedString(body.submissionId, 36);
  if (!submissionId || !UUID_PATTERN.test(submissionId)) {
    return { ok: false, error: "Identificador da conversa inválido." };
  }

  if (!Array.isArray(body.services)) {
    return { ok: false, error: "Escolha ao menos um serviço." };
  }
  const services = [
    ...new Set(
      body.services.filter(
        (service): service is ChatService =>
          typeof service === "string" && isOneOf(service, CHAT_SERVICE_OPTIONS),
      ),
    ),
  ];
  if (services.length < 1 || services.length > CHAT_SERVICE_OPTIONS.length) {
    return { ok: false, error: "Escolha ao menos um serviço válido." };
  }
  if (services.length !== body.services.length) {
    return { ok: false, error: "A lista de serviços contém opções inválidas." };
  }

  const project = trimmedString(body.project, 1000);
  if (!project || project.length < 10) {
    return { ok: false, error: "Conte um pouco mais sobre o projeto." };
  }

  const timeframe = trimmedString(body.timeframe, 40);
  if (!timeframe || !isOneOf(timeframe, CHAT_TIMEFRAME_OPTIONS)) {
    return { ok: false, error: "Selecione um prazo válido." };
  }

  const budget = trimmedString(body.budget, 40);
  if (!budget || !isOneOf(budget, CHAT_BUDGET_OPTIONS)) {
    return { ok: false, error: "Selecione uma faixa de investimento válida." };
  }

  const name = trimmedString(body.name, 80);
  if (!name || name.length < 2) {
    return { ok: false, error: "Informe seu nome." };
  }

  const phone = trimmedString(body.phone, 30);
  if (!phone || !isValidBrazilianPhone(phone)) {
    return { ok: false, error: "Informe um telefone brasileiro válido." };
  }

  const email = trimmedString(body.email, 120);
  if (email === null || (email && !EMAIL_PATTERN.test(email))) {
    return { ok: false, error: "Informe um e-mail válido ou deixe em branco." };
  }

  const pageUrlText = trimmedString(body.pageUrl, 500);
  if (!pageUrlText) return { ok: false, error: "Página de origem inválida." };
  let pageUrl: URL;
  try {
    pageUrl = new URL(pageUrlText);
  } catch {
    return { ok: false, error: "Página de origem inválida." };
  }
  const productionPage =
    pageUrl.protocol === "https:" &&
    (CHAT_ALLOWED_PAGE_HOSTS as readonly string[]).includes(pageUrl.hostname);
  const allowedLocalPage =
    (CHAT_ALLOWED_LOCAL_PAGE_ORIGINS as readonly string[]).includes(pageUrl.origin);
  const developmentPage =
    allowDevelopmentPages &&
    (pageUrl.hostname === "localhost" || pageUrl.hostname === "127.0.0.1") &&
    (pageUrl.protocol === "http:" || pageUrl.protocol === "https:");
  if (!productionPage && !allowedLocalPage && !developmentPage) {
    return { ok: false, error: "Página de origem não autorizada." };
  }
  pageUrl.hash = "";

  return {
    ok: true,
    value: {
      submissionId,
      services,
      project,
      timeframe,
      budget,
      name,
      phone,
      email,
      pageUrl: pageUrl.toString(),
    },
  };
};

const brtDate = (date: Date): string => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((item) => item.type === type)?.value ?? "";
  return part("year") + "-" + part("month") + "-" + part("day");
};

const brtTime = (date: Date): string =>
  new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

const transcriptPair = (
  question: string,
  answer: string,
  time: string,
): MessageThread["thread"] => {
  const pair: Array<MessageThread["thread"][number]> = [];
  if (question.trim()) pair.push({ from: "me", text: question, time });
  pair.push({ from: "them", text: answer, time });
  return pair;
};

export const buildChatRecords = (
  submission: ChatLeadSubmission,
  questions: ChatWidgetQuestions,
  now = new Date(),
): ChatRecordBundle => {
  const suffix = submission.submissionId;
  const servicesText = submission.services.join(", ");
  const contactText = [submission.phone, submission.email].filter(Boolean).join(" · ");
  const summary = [
    "Serviços: " + servicesText,
    "Projeto: " + submission.project,
    "Prazo: " + submission.timeframe,
    "Investimento: " + submission.budget,
    "Contato: " + contactText,
    "Página: " + submission.pageUrl,
  ].join("\n");
  const time = brtTime(now);
  const thread = [
    ...transcriptPair(questions.services, servicesText, time),
    ...transcriptPair(questions.project, submission.project, time),
    ...transcriptPair(questions.timeframe, submission.timeframe, time),
    ...transcriptPair(questions.budget, submission.budget, time),
    ...transcriptPair(questions.name, submission.name, time),
    ...transcriptPair(questions.phone, submission.phone, time),
    ...transcriptPair(
      questions.email,
      submission.email || "Não informado",
      time,
    ),
  ];

  return {
    lead: {
      id: "l_chat_" + suffix,
      name: submission.name,
      source: CHAT_WIDGET_SOURCE,
      stage: "Novo lead",
      value: "R$ 0",
      estimatedValueCents: 0,
      contactId: `c_chat_${suffix}`,
      note: summary,
      createdAt: brtDate(now),
      updatedAt: now.toISOString(),
    },
    contact: {
      id: "c_chat_" + suffix,
      name: submission.name,
      phone: submission.phone,
      email: submission.email ?? "",
      source: CHAT_WIDGET_SOURCE,
      notes: "Briefing recebido pelo chat do site.\n" + summary,
      createdAt: brtDate(now),
      status: 'prospect',
      clientSince: null,
      updatedAt: now.toISOString(),
    },
    message: {
      id: "m_chat_" + suffix,
      from: submission.name,
      channel: CHAT_WIDGET_SOURCE,
      read: false,
      createdAt: now.toISOString(),
      preview:
        submission.project.length > 90
          ? submission.project.slice(0, 90) + "…"
          : submission.project,
      thread,
    },
  };
};
